import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function Brands() {
  const [taxonomy, setTaxonomy] = useState({ brands: [] });

  useEffect(() => {
    document.title = "Brands — THE GOOD GRID";
    api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="brands-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>House of Brands</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight max-w-3xl">
            A short list, <span className="italic text-champagne-light">deliberately.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/50 mt-6 max-w-xl text-sm leading-relaxed">
            We work with a focused set of trusted brands so every product we recommend has earned its place. Brand names belong to their respective owners.
          </p>
        </Reveal>

        <div className="mt-16 space-y-6">
          {taxonomy.brands.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.1}>
              <div data-testid={`brands-row-${b.name.toLowerCase()}`} className="group relative border border-white/10 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-5 relative h-64 lg:h-[380px] overflow-hidden">
                    <img src={b.image} alt={`${b.name} products`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-[1.05] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#121212]/40" />
                  </div>
                  <div className="lg:col-span-7 p-8 lg:p-14 flex flex-col justify-center">
                    <p className="font-mono text-xs text-champagne tracking-[0.25em] mb-4">{String(i + 1).padStart(2, "0")}</p>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-[0.06em]">{b.name}</h2>
                    <p className="text-ivory/50 mt-4 max-w-md">{b.tagline}</p>
                    {b.subcategories?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-6">
                        {b.subcategories.map((s) => (
                          <Link
                            key={s}
                            to={`/collections?brand=${encodeURIComponent(b.name)}&category=${encodeURIComponent(s)}`}
                            data-testid={`brand-subcat-${b.name.toLowerCase()}-${s.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-[10px] font-mono uppercase tracking-[0.15em] border border-white/15 text-ivory/60 px-3 py-1.5 hover:border-champagne hover:text-champagne transition-colors"
                          >
                            {s}
                          </Link>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-6 mt-8">
                      <Link
                        to={`/collections?brand=${encodeURIComponent(b.name)}`}
                        data-testid={`brand-explore-${b.name.toLowerCase()}`}
                        className="group/link inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-champagne"
                      >
                        Explore {b.name} <ArrowUpRight size={14} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                      </Link>
                      {b.name === "VENUS" && (
                        <Link
                          to="/venus-gifting"
                          data-testid="brand-venus-gift-sets"
                          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors"
                        >
                          Venus Gift Sets <ArrowUpRight size={14} />
                        </Link>
                      )}
                      {b.name === "AGARO" && (
                        <Link
                          to="/agaro-collection"
                          data-testid="brand-agaro-collection"
                          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors"
                        >
                          AGARO Collection <ArrowUpRight size={14} />
                        </Link>
                      )}
                      {b.name === "CELLO" && (
                        <Link
                          to="/cello-collection"
                          data-testid="brand-cello-collection"
                          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors"
                        >
                          Cello Collection <ArrowUpRight size={14} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
